export const CREDIT_SYSTEM_GUIDE = `# Credit System & Billing Guide

## Overview

VoiceThera uses a flexible credit system that supports multiple user types and billing models. Credits are consumed when generating meditation sessions, with **10 credits per 3-minute session** (approximately 3.33 credits per minute).

Understanding the credit system helps you:
- Choose the right subscription tier for your needs
- Track credit usage and billing
- Understand when organization vs personal credits are used
- Maximize value from your investment

---

## Credit Model

### Session Credit Costs

| Session Length | Credits Required |
|----------------|------------------|
| 3 minutes | 10 credits |
| 5 minutes | 17 credits |
| 7 minutes | 23 credits |
| 10 minutes | 33 credits |

**Examples:**
- 10 credits → ~1 (3-minute) session
- 20 credits → ~2 (3-minute) sessions
- 60 credits → ~6 (3-minute) sessions
- 100 credits → ~10 (3-minute) sessions

---

## Credit Types

### Free Signup Credits

New users receive welcome credits upon account creation:

- **Individual Users**: 60 credits (enough for ~6 three-minute sessions)
- **Practitioners**: 120 credits (enough for ~12 three-minute sessions)

These credits never expire and are consumed first before any other credit type.

### Monthly Subscription Credits

Subscription plans include monthly credits that:
- Reset at the start of each billing cycle
- **Do not roll over** to the next month (use them or lose them)
- Are consumed after free credits but before purchased credits
- Vary by subscription tier (see below)

### Purchased Credit Packs

One-time credit purchases that:
- **Never expire**
- Can be used anytime
- Are consumed last (after free and monthly credits)
- Offer volume discounts on higher tiers

### Organization Bulk Credits

Organizations purchase credits in bulk and allocate them to employees:
- No subscription model for organizations
- Purchased via enterprise credit packs
- Allocated as monthly allowances to employees
- Unallocated credits remain in organizational reserve

---

## Subscription Tiers

### Individual Plans

| Plan | Price/Month | Monthly Credits | Est. 3-Min Sessions | Cost Per Session |
|------|-------------|----------------|---------------------|------------------|
| **Free** | $0 | 0 credits | 0 sessions | N/A |
| **Basic** | $29 | 100 credits | ~10 sessions | ~$2.90 |
| **Pro** | $79 | 300 credits | ~30 sessions | ~$2.63 |

**Individual Benefits:**
- ✅ Unlimited personal sessions (with credits)
- ✅ Access to all meditation categories
- ✅ Session history and favorites
- ✅ Group creation limits: Free (1 group), Basic (4 groups), Pro (10 groups)

### Practitioner Plans

| Plan | Price/Month | Monthly Credits | Est. 3-Min Sessions | Cost Per Session |
|------|-------------|----------------|---------------------|------------------|
| **Free** | $0 | 0 credits | 0 sessions | N/A |
| **Basic** | $49 | 200 credits | ~20 sessions | ~$2.45 |
| **Pro** | $129 | 600 credits | ~60 sessions | ~$2.15 |

**Practitioner Benefits:**
- ✅ Everything in Individual plans
- ✅ Client management (up to 100 active clients)
- ✅ Literal script mode for exact therapeutic delivery
- ✅ Practitioner acknowledgment in sessions
- ✅ Session delivery via shareable links
- ✅ Client feedback tracking
- ✅ Organization access grants (when approved by org admins)
- ✅ Group creation limits: Free (1 group), Basic (4 groups), Pro (unlimited groups)

### Subscription Notes

- Monthly credits reset on your billing date (e.g., if you subscribe on the 15th, credits reset on the 15th each month)
- Unused monthly credits do not roll over
- Subscriptions can be cancelled anytime (cancellation takes effect at end of billing period)
- Downgrading plans may affect group limits - excess groups become inactive

---

## Credit Packs (One-Time Purchases)

All users (Individual and Practitioner) can purchase credit packs:

| Pack | Price | Base Credits | Bonus Credits | Total Credits | Cost Per Credit | Est. 3-Min Sessions |
|------|-------|--------------|---------------|---------------|-----------------|---------------------|
| **Starter** | $10 | 100 | 0 (0%) | 100 | $0.10 | ~10 sessions |
| **Value** | $27 | 300 | 30 (+10%) | 330 | $0.082 | ~33 sessions |
| **Popular** | $49 | 600 | 90 (+15%) | 690 | $0.071 | ~69 sessions |
| **Pro** | $84 | 1200 | 240 (+20%) | 1440 | $0.058 | ~144 sessions |
| **Ultimate** | $150 | 2500 | 625 (+25%) | 3125 | $0.048 | ~312 sessions |

**Benefits:**
- ✅ **Never expire** - use them whenever you need
- ✅ **Volume discounts** - higher tiers offer better value
- ✅ **Stack with subscriptions** - purchased credits supplement monthly credits
- ✅ **Flexibility** - no recurring commitment

### Organization Credit Packs

Organizations use a bulk purchase model (no subscriptions):

| Pack | Price | Credits | Cost Per Credit | Est. 3-Min Sessions |
|------|-------|---------|-----------------|---------------------|
| **Starter Org** | $250 | 2,500 | $0.10 | ~250 sessions |
| **Growth Org** | $450 | 5,000 | $0.09 | ~500 sessions |
| **Business Org** | $850 | 10,000 | $0.085 | ~1,000 sessions |
| **Enterprise Org** | $1,500 | 20,000 | $0.075 | ~2,000 sessions |
| **Enterprise Plus** | $2,500 | 35,000 | $0.071 | ~3,500 sessions |
| **Corporate** | $4,000 | 60,000 | $0.067 | ~6,000 sessions |
| **Corporate Plus** | $6,000 | 100,000 | $0.060 | ~10,000 sessions |
| **Enterprise Max** | $8,000 | 150,000 | $0.053 | ~15,000 sessions |

Organizations allocate these credits as monthly allowances to individual employees.

---

## Credit Consumption Paths

Understanding **which credit pool** is used depends on the session context:

### Personal Mode (Individual Users & Practitioners)

When you create a session for yourself:
- ✅ **Uses your personal credit pool** (practitioner_credits table)
- Credit consumption order: Free signup credits → Monthly subscription credits → Purchased credits
- Visible in your personal dashboard

### Employee Mode (Organization Members)

When you create a session as an organization employee:
- ✅ **Uses organization credit pool** (organization_credits table)
- Credits deducted from your monthly employee allowance
- If allowance exceeded, session fails (contact org admin)
- Tracked in organization analytics
- Sessions tagged with \`created_as_employee: true\`

### Practitioner-Client Sessions

When a practitioner creates a session for a client:

**Client Linked to Organization:**
- ✅ **Uses organization credit pool**
- Requires organization admin to have granted practitioner access
- Client must be linked to an organization employee
- Organization pays for the session
- Sessions tagged with \`organization_id\` and \`created_as_employee: false\`

**Client NOT Linked to Organization:**
- ✅ **Uses practitioner's personal credit pool**
- Practitioner pays for the session
- Standard practitioner_credits consumption

**Visual Indicator:**
When selecting a client in the session creation form, you'll see:
- 🏢 "Organization credits will be used" (if org-linked)
- 👤 "Personal credits will be used" (if not org-linked)

---

## Testing Mode (Pre-Production Only)

**IMPORTANT**: The application is currently in pre-production testing phase.

**Testing Mode Flags:**
- Located in \`src/hooks/usePractitionerCredits.ts\` and \`src/hooks/useSessionPayment.ts\`
- \`TESTING_MODE = true\` (currently active)
- When enabled:
  - All credit checks bypassed
  - Practitioners see "999 free sessions remaining"
  - No credits deducted for any session
  - Credit purchase flow disabled

**Production Launch:**
- Testing mode will be set to \`TESTING_MODE = false\`
- All credit consumption logic will activate
- Practitioners will need active credits or subscriptions

If you see unlimited credits, the app is still in testing mode.

---

## Transaction Tracking

### Personal Credit History

View your credit usage:
1. Navigate to Dashboard → Credits
2. See transaction log with:
   - Date and time
   - Credits consumed
   - Session title and duration
   - Transaction type (personal session, client session, etc.)
   - Remaining balance

### Organization Credit Analytics

Organization admins can view detailed analytics:
1. Navigate to Organization Dashboard → Credits & Usage
2. See metrics:
   - Total credits purchased
   - Credits allocated to employees
   - Unallocated reserve
   - Credits used this month
   - Burn rate (credits per day)
   - Per-employee breakdown
   - Session transaction logs

Transactions are categorized:
- **Employee Sessions**: Sessions created by employees in employee mode
- **Practitioner Sessions**: Sessions created by practitioners for org-linked clients

---

## Expiration Rules

| Credit Type | Expiration Policy |
|------------|-------------------|
| Free Signup Credits | ✅ **Never expire** |
| Monthly Subscription Credits | ❌ **Expire at end of billing cycle** (use or lose) |
| Purchased Credit Packs | ✅ **Never expire** |
| Organization Bulk Credits | ✅ **Never expire** (remain in org pool) |
| Employee Monthly Allowances | ❌ **Reset on allowance date** (use or lose) |

**Strategy Tips:**
- Use monthly credits first (they expire)
- Save purchased credits for lean months or unexpected needs
- Organizations: Set realistic employee allowances to minimize waste

---

## Frequently Asked Questions

### General

**Q: How many credits does a session cost?**
A: 10 credits per 3-minute session (approximately 3.33 credits per minute). Examples:
- 3-minute session = 10 credits
- 5-minute session = 17 credits
- 7-minute session = 23 credits
- 10-minute session = 33 credits

**Q: Can I buy credits without a subscription?**
A: Yes! Credit packs are available to all users and never expire. You can use VoiceThera with only purchased credits (no subscription required).

**Q: What happens if I run out of credits?**
A: You cannot generate new sessions until you purchase more credits or your subscription renews. Existing sessions remain accessible.

**Q: Can I transfer credits between accounts?**
A: No, credits are tied to individual accounts and cannot be transferred.

### Subscriptions

**Q: Can I cancel my subscription anytime?**
A: Yes, cancellation takes effect at the end of your current billing period. You keep access to monthly credits until the period ends.

**Q: What happens to unused monthly credits?**
A: They expire at the end of the billing cycle and do not roll over. Purchased credits never expire.

**Q: Can I change plans mid-month?**
A: Yes, you can upgrade or downgrade. Upgrades take effect immediately with prorated billing. Downgrades take effect at the next billing cycle.

**Q: Do I lose my purchased credits if I cancel my subscription?**
A: No, purchased credits remain in your account permanently, even if you cancel your subscription.

### Organizations

**Q: How do organizations purchase credits?**
A: Organizations purchase bulk credit packs (no subscriptions). Credits are allocated to employees as monthly allowances.

**Q: What happens if an employee doesn't use their full allowance?**
A: Unused allowances expire at the end of the month. Credits return to the organization's unallocated reserve.

**Q: Can employees purchase their own credits?**
A: Yes, employees can maintain personal credit pools and switch between personal and employee mode when creating sessions.

**Q: How do practitioners bill organization clients?**
A: When a practitioner creates a session for an org-linked client, the organization's credit pool is automatically charged. The practitioner does not pay.

### Practitioners

**Q: Do I get charged credits when creating sessions for clients?**
A: Only if the client is NOT linked to an organization. Organization-linked clients use the organization's credits.

**Q: Can I see which credit pool will be used before generating?**
A: Yes, the session creation form shows a visual indicator: "Organization credits will be used" or "Personal credits will be used".

**Q: What if I create a session for an org-linked client but the org is out of credits?**
A: The session will fail. Contact the organization admin to purchase more credits or wait until their next allocation cycle.

---

## Support

For billing questions, subscription changes, or credit issues:
- Email: billing@voicethera.com
- Chat: Use in-app support chat
- Additional docs: See Client Management Guide for organization linking

**Last Updated**: January 2025
**Version**: 2.0
`;
